import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Download, Flame, Mail, MailCheck, RefreshCw, UserPlus } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageContainer } from '../../../common/components/PageContainer';
import { useAnalyticsApi } from '../../../common/hooks/use-analytics-api.hook';
import { Lead, useLeadsApi } from '../../../common/hooks/use-leads-api.hook';
import { useVendorPlan } from '../../../common/hooks/use-vendor-plan.hook';
import { usePlansApi } from '../../../common/hooks/use-plans-api.hook';
import { LimitBadge } from '../../../components/LimitBadge';
import { UnlockRows } from '../../../components/design-system/plan/UnlockRows';
import { HistoryLimitNotice, useHistoryWindow } from '../../../components/design-system/plan/HistoryLimitNotice';
import { PeriodControl, PeriodValue, RangeKey, ThumbStack, fitPeriod, resolvePeriod } from '../../../components/design-system/analytics';
import {
  EmptyState,
  KpiCard,
  PillTabs,
  RelativeTime,
  SearchField,
  SectionCard,
  TablePagination,
  formatNumber,
} from '../../../components/design-system/admin';
import { VizHeader } from '../../../modules/vendor/analytics/viz/components/VizHeader';
import { ReadingCard } from '../../../modules/vendor/analytics/viz/components/ReadingCard';
import { LeadReading, leadSource, rankLeads, readLead, summarizeLeads } from '../../../modules/vendor/analytics/viz/lead-temperature';
import { commonProduct, leadsReading } from '../../../modules/vendor/analytics/viz/leads-reading';
import { deltaOf, pairOf, valueOf } from '../../../modules/vendor/analytics/viz/metrics';
import { useLoader } from '../../../modules/vendor/analytics/viz/use-loader';
import { VIZ_LINKS } from '../../../modules/vendor/analytics/viz/links';
import { download } from '../analytics/components/DashboardExportMenu';
import { LeadSheet } from './components/LeadSheet';
import { TemperatureBadge } from './components/TemperatureBadge';
import { LEAD_CSV_COLUMNS, leadsCsv } from './components/leads-csv';

type Filter = 'all' | 'hot' | 'warm' | 'cold' | 'waiting' | 'contacted';
const FILTERS: Filter[] = ['all', 'waiting', 'hot', 'warm', 'cold', 'contacted'];
const LEADS_LIMIT = 500;

const isWaiting = (r?: LeadReading) => !!r && (r.action === 'email_now' || r.action === 'follow_up' || r.action === 'reply');

const matches = (filter: Filter, r?: LeadReading) => {
  if (!r) return false;
  switch (filter) {
    case 'all':
      return true;
    case 'waiting':
      return isWaiting(r);
    case 'contacted':
      return r.contacted;
    default:
      return r.temperature === filter;
  }
};

/**
 * The people who left their address to download a room, ranked by how much a
 * message would be worth today — with what they looked at, where they came
 * from, and the next thing to do about each.
 */
const LEADS_PRESETS: Exclude<RangeKey, 'custom'>[] = ['7d', '30d', '90d', '365d'];

export const Leads = () => {
  const { t } = useTranslation();
  const analytics = useAnalyticsApi();
  const leadsApi = useLeadsApi();
  const plansApi = usePlansApi();
  const { getLimit } = useVendorPlan();
  const [params, setParams] = useSearchParams();
  const rawFilter = params.get('filter') as Filter | null;
  const filter: Filter = rawFilter && FILTERS.includes(rawFilter) ? rawFilter : 'all';
  const [range, setRange] = useState<PeriodValue>({ key: '90d' });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [leadsUsed, setLeadsUsed] = useState<number | null>(null);

  // The plan's history window: a range reaching past it reads as the longest one inside it.
  const { maxDays } = useHistoryWindow();
  const shown = useMemo(() => fitPeriod(range, maxDays, LEADS_PRESETS) as PeriodValue, [range, maxDays]);
  const period = useMemo(() => resolvePeriod(shown.key, { start: shown.start, end: shown.end }), [shown]);

  // Leads used this billing period, as the plan counts them — not the rows shown.
  useEffect(() => {
    let alive = true;
    plansApi
      .getMyPlan()
      .then((r: any) => alive && setLeadsUsed(r?.usage?.maxLeads?.current ?? null))
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, loading, refreshing, error, reload } = useLoader(async () => {
    const [list, newLeads] = await Promise.all([
      leadsApi.findAllLeads({ limit: LEADS_LIMIT, sortBy: 'lastSeenAt', sortOrder: 'desc' }),
      analytics.queryMetric({ metric: 'new_leads', startDate: period.startDate, endDate: period.endDate, compareToPreviousPeriod: true }),
    ]);
    return { list, newLeads };
  }, [period.startDate, period.endDate]);

  const all = useMemo(() => data?.list?.data ?? [], [data]);
  const navigate = useNavigate();
  // `?lead=<id>` — where a "new lead" or "lead returned" notification points.
  // Opens that lead's sheet once the list is in; a lead the list does not
  // carry (another period, the plan's cap) opens as its lead file instead.
  const deepLead = params.get('lead');
  useEffect(() => {
    if (!deepLead || !data) return;
    if (all.some((l) => l._id === deepLead)) setSelectedId(deepLead);
    else navigate(`/sales/leads/${deepLead}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLead, data]);
  const closeSheet = () => {
    setSelectedId(null);
    if (params.get('lead')) {
      const next = new URLSearchParams(params);
      next.delete('lead');
      setParams(next, { replace: true });
    }
  };
  const leadCap = getLimit('maxLeads');
  const leadsCollected = leadsUsed ?? all.length;
  // The period decides whose activity counts: seen inside it.
  const inPeriod = useMemo(
    () =>
      all.filter((l) => {
        if (!l.lastSeenAt) return false;
        const day = new Date(l.lastSeenAt).toISOString().slice(0, 10);
        return day >= period.startDate && day <= period.endDate;
      }),
    [all, period.startDate, period.endDate],
  );
  const readings = useMemo(() => new Map(all.map((l) => [l._id, readLead(l)])), [all]);
  const ranked = useMemo(() => rankLeads(inPeriod, readings), [inPeriod, readings]);
  const pipeline = useMemo(() => summarizeLeads(inPeriod, readings, period), [inPeriod, readings, period]);
  const hotWaiting = useMemo(() => inPeriod.filter((l) => readings.get(l._id)?.action === 'email_now').length, [inPeriod, readings]);
  const locked = data?.list?.lockedCount ?? 0;

  const reading = useMemo(
    () =>
      data
        ? leadsReading({ pipeline, hotWaiting, newLeads: pairOf(data.newLeads), locked, topProduct: commonProduct(inPeriod) })
        : [],
    [data, pipeline, hotWaiting, locked, inPeriod],
  );

  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: 0, waiting: 0, hot: 0, warm: 0, cold: 0, contacted: 0 };
    for (const l of inPeriod) FILTERS.forEach((f) => matches(f, readings.get(l._id)) && (c[f] += 1));
    return c;
  }, [inPeriod, readings]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ranked.filter(
      (l) =>
        matches(filter, readings.get(l._id)) &&
        (!q || l.email.toLowerCase().includes(q) || (l.products ?? []).some((p) => (p.title || '').toLowerCase().includes(q))),
    );
  }, [ranked, readings, filter, search]);
  const pageRows = visible.slice(page * pageSize, page * pageSize + pageSize);
  const selected = selectedId ? all.find((l) => l._id === selectedId) ?? null : null;

  const setFilter = (next: Filter) => {
    const updated = new URLSearchParams(params);
    if (next === 'all') updated.delete('filter');
    else updated.set('filter', next);
    setParams(updated, { replace: true });
    setPage(0);
  };

  const exportCsv = () => {
    const csv = leadsCsv(
      visible,
      readings,
      LEAD_CSV_COLUMNS.map((c) => t(`VIZ_ANALYTICS.LEADS.CSV.${c}`)),
      {
        temperature: (temp) => t(`VIZ_ANALYTICS.TEMPERATURE.${temp.toUpperCase()}`),
        yes: t('VIZ_ANALYTICS.COMMON.YES'),
        no: t('VIZ_ANALYTICS.COMMON.NO'),
      },
    );
    download(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `leads_${period.startDate}_${period.endDate}.csv`);
  };

  const vsPrevious = t('VIZ_ANALYTICS.COMMON.VS_PREVIOUS', { count: period.days });
  const noLeadsAtAll = !loading && !error && all.length === 0 && locked === 0;

  return (
    <PageContainer breadcrumbs={[{ label: t('SIDEBAR.VISUALIZER.TITLE') }, { label: t('SIDEBAR.ENGAGEMENT.LEADS') }]}>
      <div className="mx-auto flex max-w-[1200px] flex-col gap-6" data-testid="viz-leads">
        <VizHeader
          title={t('VIZ_ANALYTICS.LEADS.TITLE')}
          description={t('VIZ_ANALYTICS.LEADS.SUBTITLE')}
          actions={
            <>
              {/* Leads past the cap are kept, not refused: the chip counts the ones shown, the tooltip the ones waiting. */}
              <LimitBadge
                current={leadCap > 0 ? Math.min(leadsCollected, leadCap) : leadsCollected}
                limit={leadCap}
                label={t('PLANS.LIMIT_BADGE.LEADS', 'leads')}
                tooltip={leadCap > 0 && leadsCollected > leadCap ? t('FREE_PLAN.LEADS_SHOWN', { count: leadsCollected, limit: leadCap }) : undefined}
                compact
              />
              <PeriodControl value={shown} onChange={setRange} presets={LEADS_PRESETS} maxDays={maxDays} />
              <Button variant="outline" size="sm" className="h-8" onClick={reload} disabled={refreshing || loading} aria-label={t('VIZ_ANALYTICS.COMMON.REFRESH')}>
                <RefreshCw className={cn(refreshing && 'animate-spin')} />
              </Button>
              <Button variant="outline" size="sm" className="h-8" onClick={exportCsv} disabled={!visible.length} data-testid="leads-export">
                <Download />
                <span className="hidden sm:inline">{t('VIZ_ANALYTICS.COMMON.EXPORT_CSV')}</span>
              </Button>
            </>
          }
        />

        <HistoryLimitNotice period={period} />

        {error && (
          <Card>
            <CardContent className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">{t('VIZ_ANALYTICS.COMMON.LOAD_FAILED')}</p>
              <Button variant="outline" size="sm" onClick={reload}>
                {t('VIZ_ANALYTICS.COMMON.RETRY')}
              </Button>
            </CardContent>
          </Card>
        )}

        {noLeadsAtAll ? (
          <Card className="py-2" data-testid="leads-empty">
            <EmptyState
              icon={<UserPlus />}
              title={t('VIZ_ANALYTICS.LEADS.EMPTY_TITLE')}
              description={t('VIZ_ANALYTICS.LEADS.EMPTY_BODY')}
              action={
                <Button asChild variant="outline" size="sm">
                  <Link to={VIZ_LINKS.features}>{t('VIZ_ANALYTICS.LEADS.EMPTY_ACTION')}</Link>
                </Button>
              }
            />
          </Card>
        ) : (
          !error && (
            <>
              <ReadingCard items={reading} loading={loading} />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <KpiCard
                  testId="leads-kpi-new"
                  label={t('VIZ_ANALYTICS.LEADS.KPI_NEW')}
                  value={formatNumber(valueOf(data?.newLeads) ?? pipeline.newInPeriod)}
                  delta={deltaOf(data?.newLeads)}
                  hint={vsPrevious}
                  icon={<UserPlus />}
                  loading={loading}
                />
                <KpiCard
                  testId="leads-kpi-hot"
                  label={t('VIZ_ANALYTICS.LEADS.KPI_HOT')}
                  value={formatNumber(pipeline.hot)}
                  hint={t('VIZ_ANALYTICS.LEADS.KPI_HOT_HINT', { warm: pipeline.warm, cold: pipeline.cold })}
                  icon={<Flame />}
                  loading={loading}
                />
                <KpiCard
                  testId="leads-kpi-waiting"
                  label={t('VIZ_ANALYTICS.LEADS.KPI_WAITING')}
                  value={formatNumber(pipeline.waiting + pipeline.returned)}
                  hint={t('VIZ_ANALYTICS.LEADS.KPI_WAITING_HINT')}
                  icon={<Mail />}
                  loading={loading}
                  href={`${VIZ_LINKS.leads}?filter=waiting`}
                />
                <KpiCard
                  testId="leads-kpi-consent"
                  label={t('VIZ_ANALYTICS.LEADS.KPI_CONSENT')}
                  value={formatNumber(pipeline.consented)}
                  hint={t('VIZ_ANALYTICS.LEADS.KPI_CONSENT_HINT', { count: pipeline.total })}
                  icon={<MailCheck />}
                  loading={loading}
                />
              </div>

              <SectionCard
                title={t('VIZ_ANALYTICS.LEADS.LIST_TITLE')}
                description={t('VIZ_ANALYTICS.LEADS.LIST_DESCRIPTION')}
                flush
                testId="leads-table"
              >
                <div className="flex flex-col gap-3 px-4 pb-3 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
                  <PillTabs<Filter>
                    value={filter}
                    onChange={setFilter}
                    ariaLabel={t('VIZ_ANALYTICS.LEADS.FILTER')}
                    options={FILTERS.map((f) => ({ value: f, label: t(`VIZ_ANALYTICS.LEADS.FILTERS.${f.toUpperCase()}`), count: counts[f] }))}
                  />
                  <SearchField
                    value={search}
                    onChange={(v) => {
                      setSearch(v);
                      setPage(0);
                    }}
                    placeholder={t('VIZ_ANALYTICS.LEADS.SEARCH')}
                  />
                </div>
                <div className="border-t">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[220px] ps-4 sm:ps-6">{t('VIZ_ANALYTICS.LEADS.COL.LEAD')}</TableHead>
                        <TableHead>{t('VIZ_ANALYTICS.LEADS.COL.TEMPERATURE')}</TableHead>
                        <TableHead>{t('VIZ_ANALYTICS.LEADS.COL.PRODUCTS')}</TableHead>
                        <TableHead className="text-end">{t('VIZ_ANALYTICS.LEADS.COL.DOWNLOADS')}</TableHead>
                        <TableHead>{t('VIZ_ANALYTICS.LEADS.COL.LAST_SEEN')}</TableHead>
                        <TableHead className="pe-4 text-end sm:pe-6">{t('VIZ_ANALYTICS.LEADS.COL.NEXT')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loading
                        ? [0, 1, 2, 3, 4].map((i) => (
                            <TableRow key={i}>
                              <TableCell colSpan={6} className="ps-4 sm:ps-6">
                                <Skeleton className="h-8 w-full" />
                              </TableCell>
                            </TableRow>
                          ))
                        : pageRows.map((lead) => (
                            <LeadRow key={lead._id} lead={lead} reading={readings.get(lead._id)!} onOpen={() => setSelectedId(lead._id)} />
                          ))}
                      {!loading && pageRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                            {t(inPeriod.length ? 'VIZ_ANALYTICS.LEADS.NO_MATCH' : 'VIZ_ANALYTICS.LEADS.NONE_IN_PERIOD')}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                {/*
                  The leads the plan holds back, after the ones it shows — on the
                  full list, where they would have been; a filter cannot say
                  which of them it would have matched.
                */}
                {!loading && filter === 'all' && !search.trim() && (page + 1) * pageSize >= visible.length && (
                  <UnlockRows count={locked} noun="leads" reason="maxLeads" testId="leads-locked-rows" />
                )}
                {!loading && visible.length > pageSize && (
                  <TablePagination
                    page={page}
                    pageSize={pageSize}
                    total={visible.length}
                    onPageChange={setPage}
                    onPageSizeChange={(s) => {
                      setPageSize(s);
                      setPage(0);
                    }}
                    pageSizes={[25, 50, 100]}
                    className="border-t"
                  />
                )}
              </SectionCard>
            </>
          )
        )}
      </div>

      <LeadSheet lead={selected} reading={selected ? readings.get(selected._id) : undefined} onClose={closeSheet} />
    </PageContainer>
  );
};

function LeadRow({ lead, reading, onOpen }: { lead: Lead; reading: LeadReading; onOpen: () => void }) {
  const { t } = useTranslation();
  const source = leadSource(lead);
  const canWrite = reading.action !== 'none';
  return (
    <TableRow className="cursor-pointer" onClick={onOpen} data-temperature={reading.temperature}>
      <TableCell className="ps-4 sm:ps-6">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-muted text-xs font-medium" aria-hidden>
            {lead.email.charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm">{lead.email}</p>
            <p className="truncate text-xs text-muted-foreground">
              {[source, lead.marketingConsent && !lead.unsubscribedAt ? t('VIZ_ANALYTICS.LEADS.CAN_EMAIL') : null].filter(Boolean).join(' · ') ||
                t('VIZ_ANALYTICS.LEADS.DIRECT')}
            </p>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <TemperatureBadge temperature={reading.temperature} />
      </TableCell>
      <TableCell>
        <ThumbStack items={(lead.products ?? []).map((p) => ({ key: p.productId, src: p.thumbnailPath, alt: p.title }))} max={4} size="xs" />
      </TableCell>
      <TableCell className="text-end tabular-nums">{formatNumber(lead.downloadCount ?? 0)}</TableCell>
      <TableCell className="whitespace-nowrap text-muted-foreground">
        <RelativeTime value={lead.lastSeenAt} />
      </TableCell>
      <TableCell className="pe-4 text-end sm:pe-6">
        {canWrite ? (
          <Button
            asChild
            variant="outline"
            size="sm"
            className={cn(reading.action === 'wait' || reading.action === 'nurture' ? 'text-muted-foreground' : undefined)}
            onClick={(e) => e.stopPropagation()}
          >
            <Link to={VIZ_LINKS.compose(lead.email)}>
              <Mail />
              {t(`VIZ_ANALYTICS.LEADS.ACTION.${reading.action.toUpperCase()}`)}
            </Link>
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">{t('VIZ_ANALYTICS.LEADS.ACTION.NONE')}</span>
        )}
      </TableCell>
    </TableRow>
  );
}

export default Leads;
